'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import ToolFavoriteButton from '@/components/tools/ToolFavoriteButton';
import type { AccountToolsLookup } from '@/lib/tool-catalog-types';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

function formatTimestamp(value: string) {
  const timestamp = Date.parse(value);

  if (!Number.isFinite(timestamp)) {
    return 'Recently';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(timestamp);
}

export default function AccountToolLibraryPanel() {
  const { user } = useFirebaseAuth();
  const [lookup, setLookup] = useState<AccountToolsLookup>({
    favorites: [],
    recent: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const favoriteSlugSet = useMemo(
    () => new Set(lookup.favorites.map((item) => item.toolSlug)),
    [lookup.favorites],
  );

  useEffect(() => {
    let isCancelled = false;

    async function loadAccountTools() {
      if (!user) {
        setLookup({
          favorites: [],
          recent: [],
        });
        setErrorMessage(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await fetch('/api/account/tools', {
          method: 'GET',
          headers: {
            authorization: `Bearer ${await user.getIdToken()}`,
          },
          cache: 'no-store',
        });
        const payload = (await response.json().catch(() => ({}))) as {
          success?: boolean;
          data?: AccountToolsLookup;
          error?: unknown;
        };

        if (!response.ok || !payload.success || !payload.data) {
          throw new Error(
            typeof payload.error === 'string' ? payload.error : 'Could not load your saved tools.',
          );
        }

        if (!isCancelled) {
          setLookup(payload.data);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : 'Could not load your saved tools.',
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadAccountTools();

    return () => {
      isCancelled = true;
    };
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <div className="account-tab-stack">
      <section className="account-tab-section">
        <div className="account-section-heading">
          <h3>Favorites</h3>
        </div>

        {isLoading ? (
          <p className="account-section-note">Loading your favorites.</p>
        ) : lookup.favorites.length ? (
          <div className="account-item-list">
            {lookup.favorites.map((item) => (
              <div className="account-item-row" key={item.toolSlug}>
                <Link className="account-item-link" href={item.href}>
                  <div className="account-item-copy">
                    <strong>{item.title}</strong>
                    <p>{formatTimestamp(item.addedAt)}</p>
                  </div>
                </Link>
                <div className="account-item-actions">
                  <ToolFavoriteButton
                    toolSlug={item.toolSlug}
                    isFavorite
                    labelMode="text"
                    activeLabel="Remove"
                    inactiveLabel="Save"
                    onChange={(nextIsFavorite) => {
                      if (nextIsFavorite) {
                        return;
                      }

                      setLookup((current) => ({
                        ...current,
                        favorites: current.favorites.filter(
                          (favorite) => favorite.toolSlug !== item.toolSlug,
                        ),
                      }));
                    }}
                    onError={setErrorMessage}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="account-section-note">Nothing saved yet.</p>
        )}
      </section>

      <section className="account-tab-section">
        <div className="account-section-heading">
          <h3>Recent</h3>
        </div>

        {isLoading ? (
          <p className="account-section-note">Loading.</p>
        ) : lookup.recent.length ? (
          <div className="account-item-list">
            {lookup.recent.map((item) => (
              <Link
                className="account-item-row account-item-link"
                href={item.href}
                key={item.toolSlug}
              >
                <div className="account-item-copy">
                  <strong>{item.title}</strong>
                  <p>{formatTimestamp(item.lastUsedAt)}</p>
                </div>
                <div className="account-item-actions">
                  {favoriteSlugSet.has(item.toolSlug) ? (
                    <span className="account-inline-meta">Saved</span>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="account-section-note">Nothing opened yet.</p>
        )}
      </section>

      {errorMessage ? (
        <div className="account-inline-notice">
          <strong>Saved tools</strong>
          <p>{errorMessage}</p>
        </div>
      ) : null}
    </div>
  );
}
